import type { Component } from 'vue'

interface RichTextNode {
  type: string
  level?: number
  ordered?: boolean
  url?: string
  text?: string
  children?: RichTextNode[]
  bold?: boolean
  italic?: boolean
  underline?: boolean
  image?: any
  alt?: string
  src?: string
  width?: number
  height?: number
}

interface RenderedNode {
  component: string
  props: Record<string, any>
  text?: string
  children?: RenderedNode[]
  key: number
}

export const useRichText = () => {
  const extractTextContent = (n: RichTextNode): string =>
    (n.text || '') + (n.children?.map(extractTextContent).join('') || '')

  const hasContent = (n: RichTextNode): boolean =>
    extractTextContent(n).trim().length > 0 || 
    n.type === 'image' || 
    (n.image && (n.image.url || n.image.src)) ||
    (n.src && n.src.trim().length > 0)

  const fallbackLinkText = (url?: string): string => {
    if (!url) return 'Link'
    try {
      return new URL(url).hostname.replace(/^www\./, '')
    } catch {
      return url
    }
  }

  const getComponentForNode = (n: RichTextNode): string => {
    if (n.type === 'link' && n.url?.trim()) return 'a'
    switch (n.type) {
      case 'paragraph':
        return 'p'
      case 'heading':
        return `h${n.level}`
      case 'list':
        return n.ordered ? 'ol' : 'ul'
      case 'listItem':
        return 'li'
      case 'image':
        return 'ResponsiveImage'
      case 'quote':
      case 'blockquote':
        return 'blockquote'
      case 'code':
        return 'code'
      case 'text':
        return 'span'
      default:
        return 'div'
    }
  }

  const getPropsForNode = (n: RichTextNode): Record<string, any> => {
    const p: Record<string, any> = {}
    if (n.type === 'link' && n.url?.trim()) {
      p.href = n.url
      p.target = '_blank'
      p.rel = 'noopener noreferrer'
      p.title = n.url
    }
    if (n.type === 'image') {
      // Handle Strapi image object format
      if (n.image) {
        p.image = n.image
      }
      // Handle direct src format (from HTML parsing)
      else if (n.src) {
        p.image = {
          url: n.src,
          alt: n.alt || '',
          width: n.width,
          height: n.height
        }
      }
      // Set reasonable defaults for rich text images
      p.optimisationPreset = 'content'
      p.containerSize = 'medium'
      p.lazy = true
    }
    if (n.bold) p.style = { ...p.style, fontWeight: 'bold' }
    if (n.italic) p.style = { ...p.style, fontStyle: 'italic' }
    if (n.underline) p.style = { ...p.style, textDecoration: 'underline' }
    return p
  }

  const renderRichText = (content: unknown): RenderedNode[] => {
    if (!content) return []

    if (typeof content === 'string') {
      const doc = new DOMParser().parseFromString(content, 'text/html')
      return Array.from(doc.body.childNodes)
        .map(convertDOMNodeToRenderedNode)
        .filter(Boolean) as RenderedNode[]
    }

    if (Array.isArray(content)) {
      return content
        .filter(n => n.type === 'link' || hasContent(n) || (n.children?.length ?? 0) > 0)
        .map<RenderedNode>((n, i) => {
          // render any real children…
          const rawChildren = n.children ? renderRichText(n.children) : []
          
          // For links without content, create a fallback text child
          const children = rawChildren.length > 0 
            ? rawChildren 
            : n.type === 'link' && !hasContent(n)
              ? [{
                  component: 'span',
                  props: {},
                  text: fallbackLinkText(n.url),
                  key: i * 1000 + 999 // Ensure unique key for fallback
                } as RenderedNode]
              : undefined

          // For non-links, still use text property
          const text = n.type !== 'link' ? n.text : undefined

          return {
            component: getComponentForNode(n),
            props: getPropsForNode(n),
            text,
            children,
            key: i
          }
        })
    }

    return []
  }

  const convertDOMNodeToRenderedNode = (
    node: Node,
    key = 0
  ): RenderedNode | null => {
    if (node.nodeType === Node.TEXT_NODE) {
      const t = node.textContent?.trim()
      return t ? { component: 'span', props: {}, text: t, key } : null
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement
      const tag = el.tagName.toLowerCase()
      const children = Array.from(el.childNodes)
        .map((c, idx) => convertDOMNodeToRenderedNode(c, idx))
        .filter(Boolean) as RenderedNode[]
      const props: Record<string, any> = {}
      let text: string | undefined

      if (tag === 'a') {
        const href = el.getAttribute('href') || ''
        props.href = href
        props.target = '_blank'
        props.rel = 'noopener noreferrer'
        props.title = href
        if (!children.length && !el.textContent?.trim()) {
          text = fallbackLinkText(href)
        }
      }

      if (tag === 'img') {
        const src = el.getAttribute('src') || ''
        const alt = el.getAttribute('alt') || ''
        const width = el.getAttribute('width')
        const height = el.getAttribute('height')
        
        // Convert img to ResponsiveImage component
        props.image = {
          url: src,
          alt: alt,
          width: width ? parseInt(width) : undefined,
          height: height ? parseInt(height) : undefined
        }
        props.optimisationPreset = 'content'
        props.containerSize = 'medium'
        props.lazy = true
        
        // Override the component to use ResponsiveImage instead of img
        return {
          component: 'ResponsiveImage',
          props,
          key
        }
      }

      // Handle pre/code blocks - treat <pre><code> as code block
      if (tag === 'pre') {
        // Check if it contains a code element
        const codeElement = el.querySelector('code')
        if (codeElement) {
          const language = codeElement.getAttribute('class')?.replace('language-', '') || ''
          props.language = language
          props.class = `code-block ${language ? `language-${language}` : ''}`
        } else {
          props.class = 'code-block'
        }
      }

      // Handle standalone code elements (inline code)
      if (tag === 'code' && el.parentElement?.tagName.toLowerCase() !== 'pre') {
        props.class = 'inline-code'
      }

      return {
        component: tag,
        props,
        text,
        children: children.length ? children : undefined,
        key
      }
    }

    return null
  }

  return {
    renderRichText,
    getComponentForNode,
    getPropsForNode,
    extractTextContent,
    hasContent,
    convertDOMNodeToRenderedNode
  }
}
